import {
  type CSSProperties,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  PiArrowUpRight,
  PiFloppyDisk,
  PiPaperclip,
  PiPaperPlaneRight,
  PiStack,
} from 'react-icons/pi'

import { cn } from 'components/UI/cn'
import { Alert } from 'components/FbUI/Alert'
import { colors } from 'styles/tokens'
import { StyledEditorRHF } from 'components/Editor/EditorRHF'
import { ReactCreatableSelectWrapper } from 'components/FormUtils/ReactCreatableSelectWrapper'
import {
  buildReplyAllCcOptions,
  CcBccToggles,
  emailRecipientStyles,
  formatEmailRecipientOption,
  looksLikeEmail,
  parseEmailOption,
  RecipientRow,
  ReplyMode,
  toWireRecipients,
} from 'components/FormUtils/EmailRecipientSelect'
import {
  InlineComposeShell,
  InlineReplyHeader,
  InlineReplyRecipients,
  SummaryRecipient,
} from 'components/Email'
import { useAuth } from 'context/authentication/useAuth'
import { RecipientOption, useRecipientOptions } from 'hooks/useRecipientOptions'
import { useFileAttachments } from 'hooks/useFileAttachments'
import {
  Attachment,
  AttachmentList,
  isNewAttachment,
} from 'components/Attachments/AttachmentList'
import {
  ALLOWED_EMAIL_ATTACHMENT_TYPES,
  EMAIL_ATTACHMENT_WARNING_THRESHOLD,
  MAX_EMAIL_ATTACHMENT_SIZE,
  getAcceptString,
} from 'constants/fileTypes'
import { toast } from 'react-toastify'
import axios from 'axios'
import {
  ForwardReplyPayload,
  isSendTimeoutError,
  Reply,
  ReplyRecipient,
  ReplyToReplyPayload,
  useForwardReply,
  useReplyToReply,
  useTakeOver,
  TakeOverPayload,
} from 'features/campaigns/replies_api'
import { useGetEmailIntegrationOptions } from 'services/emailIntegrationApi'

type ComposeMode = ReplyMode

const composeSchema = z.object({
  body: z.string().min(1, 'Message body is required'),
})

type ComposeFormValues = z.infer<typeof composeSchema>

// Inline styles for the reply-composer template controls: they render INSIDE
// Quill's toolbar, whose bundled CSS forces width/height/border/float on
// descendant buttons via higher-specificity selectors than Tailwind utilities.
// Inline styles win. Colors pull from the design tokens (not raw hex) so they
// track the palette. (Mirrors the sequence builder's "Variables" trigger.)
const REPLY_TPL_PILL_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  width: 'auto',
  height: '28px',
  padding: '0 10px',
  whiteSpace: 'nowrap',
  float: 'none',
  flexShrink: 0,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: colors['neutral-300'], // DS2026 outline stroke
  borderRadius: '6px',
  background: colors.surface,
  color: colors['neutral-800'],
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
}

const REPLY_TPL_ICON_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  float: 'none',
  flexShrink: 0,
  // Longhand borderColor (not the `border` shorthand): this button toggles
  // `disabled` which overrides borderColor, and mixing shorthand + longhand
  // makes React drop the color on the enable transition, so the border would
  // fall back to `currentColor` (#4b5563) — the "dark outline" bug. Keeping it
  // a longhand matches the Templates pill's Gray-300 stroke consistently.
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: colors['neutral-300'],
  borderRadius: '6px',
  background: colors.surface,
  color: colors['neutral-600'],
  cursor: 'pointer',
}

/** "Templates" browse pill + "Save reply as template" icon for the composer
 *  toolbar (FIR-7582). */
function ReplyComposerTemplateButtons({
  onBrowse,
  onSave,
  saveDisabled,
}: {
  onBrowse: () => void
  onSave: () => void
  saveDisabled: boolean
}) {
  return (
    <div
      className="flex items-center gap-2"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <span aria-hidden className="h-5 w-px bg-line" />
      <button
        type="button"
        onClick={onBrowse}
        title="Browse reply templates"
        style={REPLY_TPL_PILL_STYLE}
      >
        <PiStack
          style={{ width: 16, height: 16, flexShrink: 0, float: 'none' }}
        />
        <span>Templates</span>
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saveDisabled}
        title={
          saveDisabled
            ? 'Write a reply to save it as a template'
            : 'Save reply as a template'
        }
        aria-label="Save reply as a template"
        style={{
          ...REPLY_TPL_ICON_STYLE,
          ...(saveDisabled
            ? {
                color: colors['neutral-400'],
                borderColor: colors['neutral-200'],
                cursor: 'not-allowed',
              }
            : {}),
        }}
      >
        <PiFloppyDisk
          style={{ width: 16, height: 16, flexShrink: 0, float: 'none' }}
        />
      </button>
    </div>
  )
}

/** Wire payload captured by the deferred-submit handle — same recipient
 *  shape the panel's own send mutations already use (`ReplyRecipient`). */
interface DeferredReplyPayload {
  message: string
  cc_emails?: Array<{ name: string; email_address: string }>
  bcc_emails?: Array<{ name: string; email_address: string }>
}

interface ReplyComposeHandle {
  /** Replace the composer body in place — no remount, so Cc/Bcc/forward-to
   *  recipients the rep already entered are preserved (FIR-7582 review). */
  setBody: (html: string) => void
  /**
   * Validate the draft and capture it as a wire payload without sending —
   * the deferred-submit caller (e.g. the recommendations card's global Apply
   * button) drives the actual API call itself. Resolves `null` when the
   * draft fails validation (empty body).
   */
  submit: () => Promise<DeferredReplyPayload | null>
}

interface ReplyComposePanelProps {
  /**
   * Optional only when `deferSend` is set — the deferred-submit caller (e.g.
   * the recommendations card) has no `Reply` object or campaign-inbox
   * context to attach to.
   */
  reply?: Reply
  mode: ComposeMode
  /**
   * When set, the reply is sent via the operator's connected Nylas mailbox
   * (FIR-6879 spike). When null/undefined, the reply goes through the
   * EmailBison alias as before.
   */
  integrationId?: number | null
  /**
   * On mount, this element is scrolled into view inside the right-pane's
   * single scroll surface so the user lands at the composer but can scroll
   * up to re-read the thread (FIR-7419).
   */
  scrollIntoViewRef?: (node: HTMLDivElement | null) => void
  /** Unused when `deferSend` is set — the panel hides its own Discard button. */
  onDiscard?: () => void
  /** Unused when `deferSend` is set — the panel hides its own Send button. */
  onSent?: () => void
  /** Called from the inline-header mode switcher (Reply / Reply All / Forward). */
  onModeChange?: (mode: ReplyMode) => void
  /** Open the reply-templates browse drawer (parent owns it). */
  onBrowseTemplates?: () => void
  /** Save the current draft as a template — receives the live body HTML. */
  onSaveAsTemplate?: (bodyHtml: string) => void
  /**
   * Skip the panel's own send mutations and hide its Send/Discard footer —
   * the caller drives submission via the imperative `submit()` handle
   * instead (e.g. the recommendations card's global Apply button). The only
   * supported mode alongside this is `'reply'` with no `reply` object.
   */
  deferSend?: boolean
  /**
   * Hide the Reply / Reply All / Forward mode-switcher header — deferred
   * callers have no thread to switch reply modes against.
   */
  hideModeSwitch?: boolean
  /** Seed the composer body (e.g. an AI-suggested reply draft). */
  initialBody?: string
}

const ReplyComposePanel = forwardRef<
  ReplyComposeHandle,
  ReplyComposePanelProps
>(function ReplyComposePanel(
  {
    reply,
    mode,
    integrationId,
    scrollIntoViewRef,
    onDiscard,
    onSent,
    onModeChange,
    onBrowseTemplates,
    onSaveAsTemplate,
    deferSend,
    hideModeSwitch,
    initialBody,
  },
  ref
) {
  const replyMutation = useReplyToReply()
  const forwardMutation = useForwardReply()
  const workEmailMutation = useTakeOver()
  const useWorkEmail = mode === 'reply' && integrationId != null
  const { data: integrationOptions } = useGetEmailIntegrationOptions()
  const workEmailLabel = useMemo(
    () => integrationOptions?.find((opt) => opt.value === integrationId)?.label,
    [integrationOptions, integrationId]
  )
  const { user } = useAuth()
  const userInitials =
    [user?.first_name, user?.last_name]
      .filter(Boolean)
      .map((n) => n![0].toUpperCase())
      .join('')
      .slice(0, 2) || 'UN'

  // Lazy initializers so reply_all's Cc pre-populate runs once at mount,
  // not on every render of the compose panel.
  const [forwardTo, setForwardTo] = useState<RecipientOption[]>([])
  // `reply_all` is unreachable in deferred mode (no `Reply` object to build a
  // Cc list from) — the `reply` guard is belt-and-suspenders for the optional
  // prop.
  const [ccTo, setCcTo] = useState<RecipientOption[]>(() =>
    mode === 'reply_all' && reply
      ? buildReplyAllCcOptions(
          reply.to_emails ?? [],
          reply.cc_emails ?? [],
          reply.sender_email_address
        )
      : []
  )
  const [bccTo, setBccTo] = useState<RecipientOption[]>([])
  // The reply-all header summary surfaces the pre-populated Cc recipients, so
  // the editable Cc row starts collapsed (FIR-7804) — the user reveals it via
  // the Cc toggle or by clicking the summary. Forward/reply have no default Cc.
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)

  // Inline post-send notice: a 'timeout' warning (optimistic message kept) or
  // an 'error' (composer re-filled for retry).
  const [sendNotice, setSendNotice] = useState<{
    kind: 'timeout' | 'error'
    text: string
  } | null>(null)

  // Attachments are only sendable on the take-over (work-email) path — it
  // sends via Nylas, which supports them. The reply/forward paths go through
  // the EmailBison client (no attachment support), so the UI below is gated
  // on `useWorkEmail`. Mirrors the company-inbox composer's attachment flow.
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const {
    fileInputRef,
    openFileSelect,
    handleFileSelect,
    createRemoveHandler,
  } = useFileAttachments({
    allowedTypes: ALLOWED_EMAIL_ATTACHMENT_TYPES,
    maxFileSize: MAX_EMAIL_ATTACHMENT_SIZE,
    maxTotalSize: MAX_EMAIL_ATTACHMENT_SIZE,
    onAdd: (newAttachments) =>
      setAttachments((prev) => [...prev, ...newAttachments]),
  })
  const handleRemoveAttachment = createRemoveHandler((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  })

  // Thread-context contacts surfaced in autocomplete regardless of search.
  // Empty in deferred mode — there's no thread to seed contacts from.
  const extraContacts = useMemo(
    () =>
      reply
        ? [...(reply.to_emails ?? []), ...(reply.cc_emails ?? [])].map((r) => ({
            email: r.email_address,
            name: r.name,
          }))
        : [],
    [reply]
  )

  const {
    options: recipientOptions,
    isLoading: isLoadingRecipients,
    setSearch: setRecipientSearch,
  } = useRecipientOptions(extraContacts, false)

  const { control, handleSubmit, watch, getValues, setValue } =
    useForm<ComposeFormValues>({
      defaultValues: { body: initialBody ?? '' },
      resolver: zodResolver(composeSchema),
      mode: 'onSubmit',
    })

  // Let the parent drop a template body into the live form without remounting
  // (a remount would reset the local Cc/Bcc/forward-to recipient state).
  // `submit` is the deferred-mode counterpart to the panel's own `onSubmit`
  // mutation flow below: it validates + captures the draft as a wire payload
  // without sending, so a caller with no `Reply`/campaign-inbox context (the
  // recommendations card's global Apply button) can drive the actual send.
  useImperativeHandle(
    ref,
    () => ({
      setBody: (html: string) => setValue('body', html),
      submit: async (): Promise<DeferredReplyPayload | null> => {
        let captured: DeferredReplyPayload | null = null
        await handleSubmit((values) => {
          // Same NBSP normalization as the self-send path (FIR-7571).
          captured = {
            message: values.body.replaceAll('&nbsp;', ' '),
            cc_emails: ccTo.length ? toWireRecipients(ccTo) : undefined,
            bcc_emails: bccTo.length ? toWireRecipients(bccTo) : undefined,
          }
        })()
        return captured
      },
    }),
    [setValue, handleSubmit, ccTo, bccTo]
  )
  // A blank Quill body serializes to "<p><br></p>"; strip tags to detect empty.
  const hasDraftBody =
    watch('body')
      .replace(/<[^>]*>/g, '')
      .trim().length > 0

  const isSending =
    replyMutation.isPending ||
    forwardMutation.isPending ||
    workEmailMutation.isPending

  const onSubmit = handleSubmit((values) => {
    // Deferred mode never sends from here — the caller drives submission via
    // the imperative `submit()` handle instead — and has no `reply` to send
    // against, so bail before touching anything below that assumes one.
    if (deferSend) return
    if (!reply) return

    const cc_emails = ccTo.length ? toWireRecipients(ccTo) : undefined
    const bcc_emails = bccTo.length ? toWireRecipients(bccTo) : undefined

    // Quill 2.0.3's getSemanticHTML() encodes every space as `&nbsp;`
    // (quill/core/editor.js does `replaceAll(' ', '&nbsp;')`), so the
    // composer body arrives with a non-breaking space between every word.
    // Sent as-is, the email never wraps and renders oddly (FIR-7571).
    // Unlike the sequence-step path, these inline replies don't round-trip
    // through the email-render layer that strips NBSP, so normalize here.
    // Only the entity form is the serializer artifact: a genuine U+00A0
    // round-trips as the raw character, not the entity, so this is exact.
    const message = values.body.replaceAll('&nbsp;', ' ')

    // Send optimistically: clear the composer but keep the panel open (close
    // only on success). On a 504 keep the shown message with a refresh notice;
    // on a real failure re-fill the composer for retry.
    const senderName = [user?.first_name, user?.last_name]
      .filter(Boolean)
      .join(' ')
    const nowIso = new Date().toISOString()
    const buildOptimistic = (toEmails: ReplyRecipient[]): Reply => ({
      ...reply,
      id: -Date.now(),
      __optimistic: true,
      folder: 'sent',
      html_body: message,
      text_body: null,
      is_read: true,
      is_interested: null,
      is_automated_reply: false,
      is_tracked_reply: false,
      date_received: nowIso,
      created_at: nowIso,
      updated_at: nowIso,
      from_name: reply.sender_email_name ?? (senderName || null),
      from_email_address: reply.sender_email_address ?? '',
      primary_to_email_address:
        toEmails[0]?.email_address ?? reply.from_email_address,
      to_emails: toEmails,
      cc_emails: cc_emails ?? [],
      bcc_emails: bcc_emails ?? [],
      attachments: [],
    })

    const prevBody = values.body
    const prevCc = ccTo
    const prevBcc = bccTo
    const prevForward = forwardTo
    const clearComposer = () => {
      setValue('body', '')
      setCcTo([])
      setBccTo([])
      setForwardTo([])
    }
    const restoreComposer = () => {
      setValue('body', prevBody)
      setCcTo(prevCc)
      setBccTo(prevBcc)
      setForwardTo(prevForward)
    }
    const handleSendError = (err: unknown) => {
      if (isSendTimeoutError(err)) {
        // Read timeout: the send may well have gone through. Keep the message
        // shown (the mutation hook leaves the optimistic entry in place) and
        // tell the user to refresh rather than inviting a double-send.
        setSendNotice({
          kind: 'timeout',
          text: 'This email took too long to confirm. Refresh in a minute to check it went through.',
        })
      } else {
        restoreComposer()
        const detail = axios.isAxiosError(err)
          ? (err.response?.data as { detail?: string } | undefined)?.detail
          : undefined
        setSendNotice({
          kind: 'error',
          text: detail ?? 'Something went wrong sending this email. Try again.',
        })
      }
    }

    setSendNotice(null)

    if (mode === 'forward') {
      if (!forwardTo.length) return
      const to_emails = toWireRecipients(forwardTo)
      const payload: ForwardReplyPayload = {
        message,
        to_emails,
        cc_emails,
        bcc_emails,
      }
      clearComposer()
      forwardMutation.mutate(
        {
          replyId: reply.id,
          payload,
          optimisticReply: buildOptimistic(to_emails),
        },
        { onSuccess: () => onSent?.(), onError: handleSendError }
      )
      return
    }

    if (useWorkEmail && integrationId != null) {
      const payload: TakeOverPayload = {
        integration_id: integrationId,
        message,
        cc_emails,
        bcc_emails,
        attachments: attachments.filter(isNewAttachment).map((a) => a.file),
      }
      if (reply.thread_id == null) {
        toast.error('Could not resolve the reply thread.')
        return
      }
      workEmailMutation.mutate(
        { threadId: reply.thread_id, payload },
        {
          onSuccess: () => {
            toast.success(`Sent from ${workEmailLabel ?? 'your work email'}`)
            onSent?.()
          },
          onError: (err) => {
            const detail = axios.isAxiosError(err)
              ? (err.response?.data as { detail?: string } | undefined)?.detail
              : undefined
            toast.error(detail ?? 'Could not send via your work email.')
          },
        }
      )
      return
    }

    const payload: ReplyToReplyPayload = {
      message,
      cc_emails,
      bcc_emails,
    }
    const replyTo: ReplyRecipient[] = [
      { name: reply.from_name ?? '', email_address: reply.from_email_address },
    ]
    clearComposer()
    replyMutation.mutate(
      {
        replyId: reply.id,
        payload,
        optimisticReply: buildOptimistic(replyTo),
      },
      { onSuccess: () => onSent?.(), onError: handleSendError }
    )
  })

  const baseSelectProps = useMemo(
    () => ({
      // Match the company-inbox compose treatment (ComposePanel): DS2026 gray
      // menu + Phosphor caret/remove/clear, compact 32px rows.
      ds2026: true as const,
      height: '32px',
      isMulti: true as const,
      options: recipientOptions,
      // react-select's native event — the inbox's CreatableSelect wrapper
      // renames this to `onSearchChange`, but we use ReactCreatableSelectWrapper
      // directly here, which forwards unknown props to react-select as-is.
      onInputChange: (input: string) => setRecipientSearch(input),
      isLoading: isLoadingRecipients,
      styles: emailRecipientStyles,
      formatOptionLabel: formatEmailRecipientOption,
      isValidNewOption: looksLikeEmail,
    }),
    [recipientOptions, isLoadingRecipients, setRecipientSearch]
  )

  /** Build a recipient row for To/Cc/Bcc — identical shape across the three. */
  const renderRow = (
    label: 'To' | 'Cc' | 'Bcc',
    value: RecipientOption[],
    setValue: React.Dispatch<React.SetStateAction<RecipientOption[]>>,
    opts: { onRemove?: () => void; trailing?: React.ReactNode } = {}
  ) => (
    <RecipientRow
      label={label}
      onRemove={opts.onRemove}
      trailing={opts.trailing}
    >
      <ReactCreatableSelectWrapper
        {...baseSelectProps}
        placeholder=""
        value={value}
        onChange={(val: unknown) => setValue((val ?? []) as RecipientOption[])}
        onCreateOption={(input: string) =>
          setValue((prev) => [...prev, { label: input, value: input }])
        }
      />
    </RecipientRow>
  )

  // Null when both toggles are already revealed so `RecipientRow` doesn't
  // render an empty trailing-slot wrapper that still eats `gap-2`.
  const toggles =
    showCc && showBcc ? null : (
      <CcBccToggles
        showCc={showCc}
        showBcc={showBcc}
        onShowCc={() => setShowCc(true)}
        onShowBcc={() => setShowBcc(true)}
      />
    )

  // No `reply` in deferred mode — the header falls back to a generic label
  // (it's hidden outright whenever `hideModeSwitch` is also set, which is the
  // case for every deferred caller today).
  const baseLabel = reply
    ? `Replying to ${reply.from_name || reply.from_email_address}`
    : 'Compose reply'
  const inlineLabel = useWorkEmail
    ? `${baseLabel} from ${workEmailLabel ?? 'your work email'}`
    : baseLabel

  // Reply-All header used to show the thread subject, which hid the To
  // recipient — the person being replied to — entirely (FIR-7804). Surface the
  // actual recipients Gmail-style instead: the original sender (the implicit
  // To) plus the Cc recipients, collapsing the tail into "+N more". The Cc
  // people drop out of the summary once their editable row is open so they
  // aren't listed twice. `reply_all` (and thus a non-empty summary) is
  // unreachable in deferred mode.
  const summaryRecipients: SummaryRecipient[] = reply
    ? [
        { name: reply.from_name ?? '', email: reply.from_email_address },
        ...(showCc ? [] : ccTo.map((opt) => parseEmailOption(opt))),
      ]
    : []
  const headerLabel =
    mode === 'reply_all' ? (
      <InlineReplyRecipients
        recipients={summaryRecipients}
        // No-op once the Cc row is open — drop the handler so the summary stops
        // rendering as an interactive button (no misleading cursor/hover).
        onExpand={showCc ? undefined : () => setShowCc(true)}
      />
    ) : (
      inlineLabel
    )

  return (
    <div ref={scrollIntoViewRef}>
      <InlineComposeShell userInitials={userInitials} flat>
        {useWorkEmail && (
          <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-neutral-500 bg-brand-subtle border-b border-neutral-200">
            <PiArrowUpRight className="size-3 text-brand" />
            <span>Taking over</span>
          </div>
        )}
        {mode !== 'forward' && !hideModeSwitch && (
          <InlineReplyHeader
            mode={mode}
            label={headerLabel}
            onModeChange={onModeChange}
            trailing={toggles}
          />
        )}

        <div className="px-3 pt-3 flex flex-col">
          {mode === 'forward' &&
            renderRow('To', forwardTo, setForwardTo, { trailing: toggles })}

          {showCc &&
            renderRow('Cc', ccTo, setCcTo, {
              onRemove: () => {
                setShowCc(false)
                setCcTo([])
              },
            })}

          {showBcc &&
            renderRow('Bcc', bccTo, setBccTo, {
              onRemove: () => {
                setShowBcc(false)
                setBccTo([])
              },
            })}

          <StyledEditorRHF
            name="body"
            control={control}
            placeholder="Write your message..."
            borderless
            // FIR-7419: target the inner `.ql-editor` for the bigger click
            // surface — `min-h` on the outer wrapper doesn't propagate to
            // Quill's own root. Outer wrapper grows with the editor so the
            // entire card stays clickable. Placeholder is pinned to the
            // editor's `!py-2 !px-2` padding so the prompt sits exactly
            // where typed text begins.
            className={cn(
              'flex flex-col',
              '[&_.ql-editor]:!py-2 [&_.ql-editor]:!px-2',
              '[&_.ql-editor]:!min-h-[200px]',
              '[&_.ql-editor]:flex-1',
              // Match the company-inbox compose body: 14px typed text and hint
              // (Quill's default ~13px makes the placeholder read too small).
              '[&_.ql-editor]:!text-base',
              '[&_.ql-editor.ql-blank::before]:!text-base',
              // Match the campaign sequence-step editor spacing
              // (FIR-7341 follow-up): zero paragraph margin so each Enter
              // is a tight single line break instead of the `Editor.css`
              // default `<p>{margin-bottom:1em}` double-spaced gap.
              '[&_.ql-editor_p]:!mb-0',
              '[&_.ql-editor.ql-blank::before]:!top-2',
              '[&_.ql-editor.ql-blank::before]:!left-2'
            )}
            additionalToolbarButtons={
              // Paperclip lives in the left formatting cluster (matches the
              // company-inbox composer), not with the right-hand actions. The
              // template buttons render after it, behind their own divider.
              <>
                {useWorkEmail && (
                  <button
                    type="button"
                    onClick={(e) => {
                      // Drop focus so the button doesn't stay hovered after
                      // the native file dialog closes (matches ComposePanel).
                      e.currentTarget.blur()
                      openFileSelect()
                    }}
                    aria-label="Attach files"
                    className="flex items-center justify-center size-7 rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 [&_svg]:size-4"
                  >
                    <PiPaperclip />
                  </button>
                )}
                {onBrowseTemplates && onSaveAsTemplate && (
                  <ReplyComposerTemplateButtons
                    onBrowse={onBrowseTemplates}
                    onSave={() => onSaveAsTemplate(getValues('body'))}
                    saveDisabled={!hasDraftBody}
                  />
                )}
              </>
            }
            // Deferred mode hides its own Send/Discard — the caller's global
            // Apply action drives submission via the imperative `submit()`
            // handle instead (`onSubmit` above is a no-op in that mode).
            toolbarSuffix={
              deferSend ? undefined : (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onDiscard}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-sm text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 transition-colors"
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    onClick={onSubmit}
                    disabled={isSending}
                    className={cn(
                      'inline-flex items-center gap-1.5 h-8 px-4 rounded-md text-sm font-medium text-white transition-colors',
                      isSending
                        ? 'bg-brand-bright/60 cursor-not-allowed'
                        : 'bg-brand-bright hover:bg-brand-active'
                    )}
                  >
                    <PiPaperPlaneRight className="size-4" />
                    {isSending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              )
            }
            toolbarClassName="sticky bottom-0 bg-white z-10"
          />

          {sendNotice && (
            <div className="pb-3">
              <Alert
                color={sendNotice.kind === 'timeout' ? 'warning' : 'danger'}
                description={sendNotice.text}
                onDismiss={() => setSendNotice(null)}
              />
            </div>
          )}

          {useWorkEmail && attachments.length > 0 && (
            <div className="pb-3">
              <AttachmentList
                attachments={attachments}
                onRemove={handleRemoveAttachment}
                maxSize={MAX_EMAIL_ATTACHMENT_SIZE}
                warningThreshold={EMAIL_ATTACHMENT_WARNING_THRESHOLD}
              />
            </div>
          )}
        </div>

        <input
          type="file"
          hidden
          multiple
          aria-label="Attach files"
          ref={fileInputRef}
          onChange={(e) => handleFileSelect(e, attachments)}
          accept={getAcceptString(ALLOWED_EMAIL_ATTACHMENT_TYPES)}
        />
      </InlineComposeShell>
    </div>
  )
})
ReplyComposePanel.displayName = 'ReplyComposePanel'

export { ReplyComposePanel }
export type { DeferredReplyPayload, ReplyComposeHandle, ReplyComposePanelProps }
